/**
 * "Sign in with Google" (Google Identity Services) for the web build. The ID token is verified by the
 * server against Google's keys. Hidden when no client id is configured and inside native apps
 * (GIS does not run in app WebViews; use a native Google sign-in plugin there).
 */
import { useEffect, useRef } from 'react';
import type { AuthResponse } from '@cardhub/protocol';
import { api } from '../api/client';
import { deviceId } from '../lib/device';
import { errorMessage } from '../lib/hooks';
import { isNative } from '../native/platform';
import { useSession } from '../store/session';
import { toast } from '../store/ui';

interface Gis {
  accounts: { id: { initialize(o: { client_id: string; callback: (r: { credential: string }) => void }): void; renderButton(el: HTMLElement, o: Record<string, unknown>): void } };
}

let scriptLoading: Promise<void> | null = null;
function loadScript(): Promise<void> {
  scriptLoading ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Google sign-in unavailable'));
    document.head.appendChild(s);
  });
  return scriptLoading;
}

export function GoogleSignIn({ onSignedIn, referralCode }: { onSignedIn: (res: AuthResponse) => void; referralCode?: string }) {
  const clientId = useSession((s) => (s.boot as { googleClientId?: string | null } | null)?.googleClientId ?? null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!clientId || isNative || !ref.current) return;
    let alive = true;
    loadScript()
      .then(() => {
        const g = (window as unknown as { google?: Gis }).google;
        if (!alive || !g || !ref.current) return;
        g.accounts.id.initialize({
          client_id: clientId,
          callback: async ({ credential }) => {
            try {
              onSignedIn(await api.post<AuthResponse>('/api/v1/auth/social', { provider: 'google', idToken: credential, deviceId: deviceId(), ...(referralCode ? { referralCode } : {}) }));
            } catch (e) {
              toast(errorMessage(e), 'error');
            }
          },
        });
        g.accounts.id.renderButton(ref.current, { theme: 'filled_black', size: 'large', shape: 'pill', width: 300, text: 'continue_with' });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [clientId, onSignedIn, referralCode]);
  if (!clientId || isNative) return null;
  return <div ref={ref} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} aria-label="Sign in with Google" />;
}
